/** Enum values shared by Prisma (requirements §10.2), the API and the web app. */

export const ROLES = ['OWNER', 'MANAGER'] as const;
export type Role = (typeof ROLES)[number];

export const SEXES = ['FEMALE', 'MALE'] as const;
export type Sex = (typeof SEXES)[number];

export const ANIMAL_SOURCES = ['OPENING_HERD', 'PURCHASED', 'BORN_ON_FARM'] as const;
export type AnimalSource = (typeof ANIMAL_SOURCES)[number];

export const ANIMAL_STATUSES = ['ACTIVE', 'SOLD', 'DEAD', 'CULLED'] as const;
export type AnimalStatus = (typeof ANIMAL_STATUSES)[number];

export const EVENT_TYPES = [
  'HEAT',
  'SERVICE',
  'PREG_CHECK',
  'CALVING',
  'DRY_OFF',
  'ABORTION',
  'TREATMENT',
  'VACCINATION',
  'DEWORMING',
  'WEIGHT',
  'NOTE',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** Event types that only make sense for females (BR-*, sprint 3 §4). */
export const FEMALE_ONLY_EVENT_TYPES: readonly EventType[] = [
  'HEAT',
  'SERVICE',
  'PREG_CHECK',
  'CALVING',
  'DRY_OFF',
  'ABORTION',
];

/** Event types that can be entered as opening history (AR-8). */
export const OPENING_EVENT_TYPES: readonly EventType[] = ['CALVING', 'DRY_OFF', 'SERVICE', 'PREG_CHECK'];

/** Event types whose cost becomes a linked expense (EX-3). */
export const COSTED_EVENT_TYPES: readonly EventType[] = [
  'SERVICE',
  'PREG_CHECK',
  'TREATMENT',
  'VACCINATION',
  'DEWORMING',
];

export const MILK_SESSIONS = ['MORNING', 'MIDDAY', 'EVENING'] as const;
export type MilkSession = (typeof MILK_SESSIONS)[number];

export const WITHHELD_REASONS = ['COLOSTRUM', 'TREATMENT', 'OTHER'] as const;
export type WithheldReason = (typeof WITHHELD_REASONS)[number];

export const REMINDER_TYPES = [
  'HEAT_WATCH',
  'PREG_CHECK_DUE',
  'RESERVE_DUE',
  'DRY_OFF_DUE',
  'CALVING_DUE',
  'BREEDING_START',
  'HEIFER_BREEDING',
  'VACCINATION_DUE',
  'DEWORMING_DUE',
] as const;
export type ReminderType = (typeof REMINDER_TYPES)[number];

export const REMINDER_STATUSES = ['OPEN', 'DONE', 'DISMISSED'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];

/** Reasons offered when a reminder is dismissed (BR-9). Stored in `reminders.note`. */
export const DISMISS_REASONS = ['NOT_NEEDED', 'DONE_OUTSIDE', 'WRONG_DATA', 'OTHER'] as const;
export type DismissReason = (typeof DISMISS_REASONS)[number];

export const SALE_TYPES = ['MILK', 'ANIMAL', 'OTHER'] as const;
export type SaleType = (typeof SALE_TYPES)[number];

export const PAYMENT_METHODS = ['CASH', 'TELEBIRR', 'BANK', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const COST_TYPES = ['OPERATING', 'CAPITAL'] as const;
export type CostType = (typeof COST_TYPES)[number];

export const PROTOCOL_TYPES = ['VACCINATION', 'DEWORMING'] as const;
export type ProtocolType = (typeof PROTOCOL_TYPES)[number];

export const PROTOCOL_TARGETS = ['ALL', 'ADULT_FEMALES', 'YOUNGSTOCK'] as const;
export type ProtocolTarget = (typeof PROTOCOL_TARGETS)[number];

export const FILE_KINDS = ['ANIMAL_PHOTO', 'RECEIPT'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

export const FILE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type FileMimeType = (typeof FILE_MIME_TYPES)[number];
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const EXPENSE_UNITS = ['KG', 'QUINTAL', 'BAG', 'LITER', 'PIECE', 'DAY', 'MONTH', 'OTHER'] as const;
export type ExpenseUnit = (typeof EXPENSE_UNITS)[number];

export const LOCALES = ['am', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const CALENDARS = ['EC', 'GC'] as const;
export type CalendarKind = (typeof CALENDARS)[number];

export const FARM_TIME_ZONE = 'Africa/Addis_Ababa';
